import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTrophy, FaCrown, FaMedal, FaStar, FaFire, FaUsers, FaSpinner, FaSync, FaGem, FaBolt, FaShieldAlt } from 'react-icons/fa';
import { getLeaderboard, getUserRank, type LeaderboardEntry as RawLeaderboardEntry } from '../lib/challenges';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';

interface LeaderboardProps {
  className?: string;
  showUserRank?: boolean;
  limit?: number;
}

/** Normalized shape the UI expects (camelCase). */
type UILeader = {
  userId: string;
  rank: number;
  profileImage?: string | null;
  fullName?: string | null;
  email?: string;
  lastActivity?: string | null;
  totalPoints: number;
  currentStreak: number;
  challengesCompleted: number;
};

/** Normalize backend entry: accept snake_case or camelCase safely. */
function normalizeEntry(e: any): UILeader | null {
  if (!e) return null;

  const userId =
    e.userId ??
    e.user_id ??
    e.id ??
    (e.user && (e.user.id ?? e.user.user_id)) ??
    '';

  if (!userId) return null;

  const profileImage =
    e.profileImage ??
    e.profile_image ??
    e.user?.profile_image ??
    e.user?.profile_image_url ??
    null;

  const fullName =
    e.fullName ??
    e.full_name ??
    e.user?.full_name ??
    null;

  const email =
    e.email ??
    e.user?.email ??
    undefined;

  const rank = Number(e.rank ?? e.position ?? 0);
  const lastActivity = e.lastActivity ?? e.last_activity ?? null;
  const totalPoints = Number(e.totalPoints ?? e.total_points ?? e.points ?? 0);
  const currentStreak = Number(e.currentStreak ?? e.current_streak ?? e.streak ?? 0);
  const challengesCompleted = Number(e.challengesCompleted ?? e.challenges_completed ?? e.completed ?? 0);

  return {
    userId,
    rank: isNaN(rank) ? 0 : rank,
    profileImage,
    fullName,
    email,
    lastActivity,
    totalPoints: isNaN(totalPoints) ? 0 : totalPoints,
    currentStreak: isNaN(currentStreak) ? 0 : currentStreak,
    challengesCompleted: isNaN(challengesCompleted) ? 0 : challengesCompleted,
  };
}

/** Safe date distance label */
function formatLastActivity(dateString?: string | null) {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  if (diffMs < 0) return '—';
  const diffInHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffInHours < 1) return 'Hace <1h';
  if (diffInHours < 24) return `Hace ${diffInHours}h`;
  const diffInDays = Math.floor(diffInHours / 24);
  return `Hace ${diffInDays}d`;
}

export default function Leaderboard({ className = '', showUserRank = true, limit = 10 }: LeaderboardProps) {
  const [leaders, setLeaders] = useState<UILeader[]>([]);
  const [userRank, setUserRank] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const rawUser = getCurrentUser();
  const user = useMemo(() => {
    if (!rawUser) return null;
    const { id, email, profile_image } = rawUser as any;
    return { id, email, profile_image };
  }, [rawUser?.id, rawUser?.email, (rawUser as any)?.profile_image]);

  const lastErrorRef = useRef<number>(0);
  const TOAST_COOLDOWN_MS = 2000;

  const showErrorToastOnce = useCallback((msg: string) => {
    const now = Date.now();
    if (now - lastErrorRef.current > TOAST_COOLDOWN_MS) {
      lastErrorRef.current = now;
      toast.error(msg);
    }
  }, []);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return (
          <motion.div
            className="relative"
            animate={{ 
              rotate: [0, 5, -5, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <FaCrown className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-yellow-400 drop-shadow-lg" />
            <motion.div
              className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-400 rounded-full"
              animate={{ 
                scale: [0, 1.2, 0],
                opacity: [0, 1, 0]
              }}
              transition={{ 
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </motion.div>
        );
      case 2:
        return (
          <motion.div
            animate={{ 
              y: [0, -2, 0],
              rotate: [0, 2, -2, 0]
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <FaMedal className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-300 drop-shadow-lg" />
          </motion.div>
        );
      case 3:
        return (
          <motion.div
            animate={{ 
              scale: [1, 1.05, 1],
              rotate: [0, -3, 3, 0]
            }}
            transition={{ 
              duration: 2.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <FaMedal className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-orange-400 drop-shadow-lg" />
          </motion.div>
        );
      default:
        return (
          <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 border border-gray-600 flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-xs sm:text-sm">#{rank}</span>
          </div>
        );
    }
  };

  const getRankBackground = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-500/30 via-yellow-400/20 to-amber-500/30 border-yellow-400/60 shadow-lg';
      case 2:
        return 'bg-gradient-to-r from-gray-400/30 via-gray-300/20 to-slate-400/30 border-gray-300/60 shadow-lg';
      case 3:
        return 'bg-gradient-to-r from-orange-500/30 via-orange-400/20 to-amber-600/30 border-orange-400/60 shadow-lg';
      default:
        return 'bg-gradient-to-r from-gray-800/40 via-gray-900/30 to-black/40 border-gray-600/40 hover:border-gray-500/60';
    }
  };

  const loadLeaderboard = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const data = await getLeaderboard(limit);
      if (signal?.aborted) return;
      const normalized: UILeader[] = Array.isArray(data)
        ? data.map(normalizeEntry).filter(Boolean) as UILeader[]
        : [];
      const withRank = normalized
        .sort((a, b) => b.totalPoints - a.totalPoints)
        .map((e, i) => ({ ...e, rank: e.rank && e.rank > 0 ? e.rank : i + 1 }));

      setLeaders(withRank);
    } catch (error: any) {
      console.error('Error loading leaderboard:', error);
      showErrorToastOnce('Failed to load leaderboard');
      setLeaders([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [limit, showErrorToastOnce]);

  const loadUserRank = useCallback(async (signal?: AbortSignal) => {
    if (!user || !showUserRank) return;
    try {
      const rank = await getUserRank(user.id);
      if (signal?.aborted) return;
      setUserRank(rank ?? null);
    } catch (error) {
      console.error('Error loading user rank:', error);
    }
  }, [user, showUserRank]);

  useEffect(() => {
    const controller = new AbortController();
    loadLeaderboard(controller.signal);
    loadUserRank(controller.signal);
    return () => controller.abort();
  }, [loadLeaderboard, loadUserRank]);

  const handleRefresh = async () => {
    setRefreshing(true);
    const controller = new AbortController();
    try {
      await Promise.all([
        loadLeaderboard(controller.signal),
        loadUserRank(controller.signal),
      ]);
      toast.success('Leaderboard actualizado');
    } catch {
      showErrorToastOnce('Error al actualizar');
    } finally {
      setRefreshing(false);
      controller.abort();
    }
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Mobile-First Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
        <motion.div 
          className="absolute -top-10 -left-10 w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 bg-gradient-to-br from-yellow-400/20 to-amber-500/10 rounded-full blur-2xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{ 
            duration: 8,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.div 
          className="absolute -bottom-10 -right-10 w-32 h-32 sm:w-40 sm:h-40 md:w-64 md:h-64 bg-gradient-to-br from-white/10 to-gray-500/5 rounded-full blur-2xl"
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ 
            duration: 12,
            repeat: Infinity,
            ease: "linear"
          }}
        />
      </div>

      {/* Main Container - Mobile Optimized */}
      <div className="relative z-10 backdrop-blur-xl bg-gradient-to-br from-black/90 via-gray-900/70 to-black/90 rounded-xl sm:rounded-2xl md:rounded-3xl border border-gray-700/50 shadow-2xl overflow-hidden">
        
        {/* Mobile-First Header */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 via-amber-500/15 to-yellow-600/20"></div>
          <div className="relative p-3 sm:p-4 md:p-6 lg:p-8 border-b border-gray-700/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              
              {/* Header Content - Mobile Stacked */}
              <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
                <motion.div 
                  className="relative flex-shrink-0"
                  animate={{ 
                    rotate: [0, 5, -5, 0],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{ 
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-yellow-400 via-amber-500 to-yellow-600 rounded-lg sm:rounded-xl md:rounded-2xl flex items-center justify-center shadow-xl border border-yellow-300/50">
                    <FaTrophy className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-black drop-shadow-lg" />
                  </div>
                  
                  <motion.div
                    className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl border-2 border-yellow-400/60"
                    animate={{ 
                      scale: [1, 1.15, 1],
                      opacity: [0.6, 0.2, 0.6]
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                </motion.div>
                
                <div className="min-w-0 flex-1">
                  <motion.h2 
                    className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-white via-yellow-200 to-white bg-clip-text text-transparent"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                  >
                    {/* Mobile: Short title, Desktop: Full title */}
                    <span className="sm:hidden">Top</span>
                    <span className="hidden sm:inline">Clasificación Global</span>
                  </motion.h2>
                  <motion.p 
                    className="text-gray-300 text-xs sm:text-sm md:text-base lg:text-lg font-medium hidden sm:block"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  >
                    Los mejores estudiantes
                  </motion.p>
                </div>
              </div>
              
              {/* Refresh Button - Mobile Optimized */}
              <motion.button
                onClick={handleRefresh}
                disabled={refreshing}
                className="w-full sm:w-auto relative group p-2.5 sm:p-3 md:p-4 rounded-lg sm:rounded-xl md:rounded-2xl bg-gradient-to-br from-gray-800/80 to-black/60 border border-gray-600/50 hover:border-yellow-400/50 transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-xl min-h-[40px] sm:min-h-[48px]"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label="Actualizar clasificación"
              >
                <div className="flex items-center justify-center">
                  <FaSync className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-gray-300 group-hover:text-yellow-400 transition-colors ${refreshing ? 'animate-spin' : ''}`} />
                  <span className="ml-1.5 sm:ml-2 text-white font-medium text-xs sm:text-sm md:text-base">Actualizar</span>
                </div>
                <div className="absolute inset-0 rounded-lg sm:rounded-xl md:rounded-2xl bg-gradient-to-r from-yellow-400/0 to-yellow-400/0 group-hover:from-yellow-400/10 group-hover:to-yellow-400/5 transition-all duration-300"></div>
              </motion.button>
            </div>
          </div>
        </div>

        {/* User Rank Section - Mobile Optimized */}
        {showUserRank && user && userRank && (
          <motion.div 
            className="relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/15 to-indigo-500/20"></div>
            <div className="relative p-3 sm:p-4 md:p-6 border-b border-gray-700/50">
              
              {/* Mobile: Stack vertically, Desktop: Side by side */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                
                {/* User Info - Mobile Optimized */}
                <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600 p-0.5 shadow-xl">
                      <div className="w-full h-full rounded-lg sm:rounded-xl md:rounded-2xl overflow-hidden">
                        {user.profile_image ? (
                          <img src={user.profile_image} alt="Tu perfil" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-600 to-purple-700 flex items-center justify-center">
                            <span className="text-white font-bold text-sm sm:text-lg">{(user.email || '?').charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <motion.div
                      className="absolute -bottom-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 bg-green-400 rounded-full border-2 border-black shadow-lg"
                      animate={{ 
                        scale: [1, 1.2, 1],
                        opacity: [0.8, 1, 0.8]
                      }}
                      transition={{ 
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </div>
                  
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-bold text-sm sm:text-base md:text-lg lg:text-xl mb-1">
                      {/* Mobile: Short text, Desktop: Full text */}
                      <span className="sm:hidden">Pos:</span>
                      <span className="hidden sm:inline">Tu Posición:</span>
                      <span className="ml-1 sm:ml-2 text-base sm:text-lg md:text-xl lg:text-2xl bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
                        #{userRank.rank ?? '—'}
                      </span>
                    </div>
                    <div className="text-gray-300 font-medium text-xs sm:text-sm md:text-base">
                      {userRank.totalPoints ?? 0} pts
                    </div>
                  </div>
                </div>
                
                {/* User Stats - Mobile Grid */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3 md:flex md:items-center md:space-x-4 md:gap-0">
                  <motion.div 
                    className="text-center group bg-white/5 rounded-lg p-2 sm:bg-transparent sm:p-0"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="flex items-center justify-center mb-1">
                      <FaFire className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-orange-400 mr-1 sm:mr-2 group-hover:text-orange-300 transition-colors" />
                      <span className="text-white font-bold text-sm sm:text-base md:text-lg">{userRank.currentStreak ?? 0}</span>
                    </div>
                    <div className="text-gray-400 text-xs sm:text-sm font-medium">Racha</div>
                  </motion.div>
                  
                  <motion.div 
                    className="text-center group bg-white/5 rounded-lg p-2 sm:bg-transparent sm:p-0"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="flex items-center justify-center mb-1">
                      <FaShieldAlt className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-blue-400 mr-1 sm:mr-2 group-hover:text-blue-300 transition-colors" />
                      <span className="text-white font-bold text-sm sm:text-base md:text-lg">{userRank.challengesCompleted ?? 0}</span>
                    </div>
                    <div className="text-gray-400 text-xs sm:text-sm font-medium">Desafíos</div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Leaderboard Content - Mobile First */}
        <div className="relative p-2 sm:p-3 md:p-6 lg:p-8">
          {loading ? (
            <div className="text-center py-6 sm:py-8 md:py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto mb-3 sm:mb-4 md:mb-6"
              >
                <div className="w-full h-full rounded-full border-4 border-gray-700 border-t-yellow-400 shadow-lg"></div>
              </motion.div>
              <p className="text-gray-300 text-sm sm:text-base md:text-lg font-medium">Cargando...</p>
            </div>
          ) : leaders.length === 0 ? (
            <div className="text-center py-6 sm:py-8 md:py-12">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, type: "spring" }}
              >
                <FaUsers className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 text-gray-600 mx-auto mb-3 sm:mb-4 md:mb-6" />
              </motion.div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 sm:mb-3">Sin Clasificaciones</h3>
              <p className="text-gray-400 text-sm sm:text-base md:text-lg">¡Sé el primero en completar un desafío!</p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3 md:space-y-4">
              <AnimatePresence>
                {leaders.map((leader, index) => (
                  <motion.div
                    key={leader.userId}
                    className={`relative overflow-hidden flex items-center p-2.5 sm:p-3 md:p-4 lg:p-6 rounded-lg sm:rounded-xl md:rounded-2xl border-2 transition-all duration-500 hover:scale-[1.01] ${getRankBackground(leader.rank)}`}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ 
                      duration: 0.6, 
                      delay: index * 0.05,
                      type: "spring",
                      stiffness: 100
                    }}
                    whileHover={{ 
                      scale: 1.01,
                      transition: { duration: 0.2 }
                    }}
                  >
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-0 right-0 w-12 h-12 sm:w-16 sm:h-16 md:w-24 md:h-24 bg-gradient-to-bl from-white/20 to-transparent rounded-full blur-xl"></div>
                    </div>
                    
                    {/* Mobile-First Layout */}
                    <div className="relative flex items-center w-full min-w-0">
                      
                      {/* Rank Icon */}
                      <div className="flex-shrink-0 mr-2 sm:mr-3 md:mr-4">
                        {getRankIcon(leader.rank)}
                      </div>

                      {/* User Avatar */}
                      <div className="relative flex-shrink-0 mr-2 sm:mr-3 md:mr-4">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl overflow-hidden ${
                          leader.rank <= 3 ? 'p-0.5 bg-gradient-to-br from-yellow-400 to-amber-500' : 'bg-gray-700'
                        } shadow-lg`}>
                          <div className="w-full h-full rounded-lg sm:rounded-xl overflow-hidden">
                            {leader.profileImage ? (
                              <img
                                src={leader.profileImage}
                                alt={leader.fullName ?? leader.email ?? 'User'}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                                <span className="text-white font-bold text-xs sm:text-sm">
                                  {((leader.fullName || leader.email || 'U').charAt(0) || '?').toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Elite Badge for Top 3 */}
                        {leader.rank <= 3 && (
                          <motion.div
                            className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg"
                            animate={{ 
                              rotate: [0, 360],
                              scale: [1, 1.1, 1]
                            }}
                            transition={{ 
                              duration: 3,
                              repeat: Infinity,
                              ease: "linear"
                            }}
                          >
                            <FaGem className="w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-3 md:h-3 text-black" />
                          </motion.div>
                        )}
                      </div>
                      
                      {/* User Info - Mobile Responsive */}
                      <div className="flex-1 min-w-0 mr-2 sm:mr-3 md:mr-4">
                        <div className={`font-bold text-sm sm:text-base md:text-lg mb-0.5 sm:mb-1 truncate ${
                          leader.rank === 1 ? 'text-yellow-300' :
                          leader.rank === 2 ? 'text-gray-200' :
                          leader.rank === 3 ? 'text-orange-300' :
                          'text-white'
                        }`}>
                          {/* Mobile: Show email if no name, Desktop: Show full name */}
                          {leader.fullName ? (
                            <>
                              <span className="sm:hidden">{leader.fullName.split(' ')[0]}</span>
                              <span className="hidden sm:inline">{leader.fullName}</span>
                            </>
                          ) : (
                            <>
                              <span className="sm:hidden">{leader.email?.split('@')[0]}</span>
                              <span className="hidden sm:inline">{leader.email}</span>
                            </>
                          )}
                        </div>
                        {/* Mobile: Hide last activity, Desktop: Show it */}
                        <div className="text-gray-400 text-xs sm:text-sm font-medium hidden sm:block">
                          {formatLastActivity(leader.lastActivity)}
                        </div>
                      </div>

                      {/* Points - Mobile Prominent */}
                      <div className="flex-shrink-0 text-right">
                        <div className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-0.5 sm:mb-1 ${
                          leader.rank === 1 ? 'text-yellow-400' :
                          leader.rank === 2 ? 'text-gray-300' :
                          leader.rank === 3 ? 'text-orange-400' :
                          'text-white'
                        }`}>
                          {leader.totalPoints}
                        </div>
                        {/* Mobile: Hide "puntos" text, Desktop: Show it */}
                        <div className="text-gray-400 text-xs sm:text-sm font-medium hidden sm:block">puntos</div>
                      </div>
                    </div>

                    {/* Mobile Stats Row - Compact display */}
                    <div className="absolute bottom-1 right-1 sm:relative sm:bottom-auto sm:right-auto sm:ml-4 flex items-center space-x-1.5 sm:space-x-2 md:space-x-3">
                      
                      {/* Streak */}
                      <motion.div 
                        className="flex items-center space-x-1 bg-orange-500/20 rounded-lg px-1.5 py-0.5 sm:px-2 sm:py-1 border border-orange-400/30"
                        whileHover={{ scale: 1.05 }}
                      >
                        <FaFire className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 text-orange-400" />
                        <div className="text-right">
                          <div className="text-white font-bold text-xs sm:text-sm">{leader.currentStreak}</div>
                        </div>
                      </motion.div>
                      
                      {/* Challenges */}
                      <motion.div 
                        className="flex items-center space-x-1 bg-blue-500/20 rounded-lg px-1.5 py-0.5 sm:px-2 sm:py-1 border border-blue-400/30"
                        whileHover={{ scale: 1.05 }}
                      >
                        <FaStar className="w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 text-blue-400" />
                        <div className="text-right">
                          <div className="text-white font-bold text-xs sm:text-sm">{leader.challengesCompleted}</div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Top 3 Position Indicator - Mobile: Smaller, Desktop: Normal */}
                    {leader.rank <= 3 && (
                      <motion.div
                        className="absolute top-1 right-1 sm:top-2 sm:right-2"
                        animate={{ 
                          scale: [1, 1.1, 1],
                          opacity: [0.7, 1, 0.7]
                        }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        <div className={`px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-xs font-bold ${
                          leader.rank === 1 ? 'bg-yellow-400/30 text-yellow-200 border border-yellow-400/50' :
                          leader.rank === 2 ? 'bg-gray-300/30 text-gray-200 border border-gray-300/50' :
                          'bg-orange-400/30 text-orange-200 border border-orange-400/50'
                        }`}>
                          {/* Mobile: Just number, Desktop: "TOP X" */}
                          <span className="sm:hidden">{leader.rank}</span>
                          <span className="hidden sm:inline">TOP {leader.rank}</span>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* Mobile-Optimized Footer */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 to-black/60"></div>
          <div className="relative p-3 sm:p-4 md:p-6 border-t border-gray-700/50">
            <div className="text-center">
              <motion.div
                className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-2 mb-2 sm:mb-3"
                animate={{ 
                  scale: [1, 1.01, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <FaTrophy className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-yellow-400" />
                <span className="text-gray-300 font-medium text-xs sm:text-sm md:text-base text-center px-2">
                  {/* Mobile: Short text, Desktop: Full text */}
                  <span className="sm:hidden">Completa desafíos para subir</span>
                  <span className="hidden sm:inline">Completa desafíos para ganar puntos y subir en la clasificación</span>
                </span>
                <FaBolt className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-yellow-400" />
              </motion.div>
              
              {/* Mobile-Friendly Animated Dots */}
              <div className="flex justify-center space-x-1 sm:space-x-2">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-1 h-1 sm:w-1.5 sm:h-1.5 md:w-2 md:h-2 bg-yellow-400/60 rounded-full"
                    animate={{ 
                      scale: [1, 1.3, 1],
                      opacity: [0.4, 1, 0.4]
                    }}
                    transition={{ 
                      duration: 1.5,
                      repeat: Infinity,
                      delay: i * 0.2,
                      ease: "easeInOut"
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Mobile-Optimized Styles */}
      <style>{`
        /* Mobile-first responsive design */
        @media (max-width: 640px) {
          .backdrop-blur-xl {
            backdrop-filter: blur(8px); /* Reduce blur for better mobile performance */
          }
          
          /* Ensure touch targets are at least 44px */
          button {
            min-height: 44px;
            min-width: 44px;
          }
          
          /* Optimize text rendering on mobile */
          .font-bold {
            font-weight: 600; /* Slightly lighter for better mobile readability */
          }
          
          /* Improve text contrast on mobile */
          .text-gray-300 {
            color: rgb(209 213 219); /* Slightly brighter for mobile screens */
          }
          
          .text-gray-400 {
            color: rgb(156 163 175); /* Better contrast on mobile */
          }
          
          /* Ensure proper spacing on very small screens */
          .space-x-2 > :not([hidden]) ~ :not([hidden]) {
            margin-left: 0.5rem;
          }
          
          .space-y-2 > :not([hidden]) ~ :not([hidden]) {
            margin-top: 0.5rem;
          }
        }

        /* Smooth animations optimized for mobile */
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 15px rgba(251, 191, 36, 0.3);
          }
          50% {
            box-shadow: 0 0 25px rgba(251, 191, 36, 0.5);
          }
        }
        
        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Improve touch responsiveness */
        @media (hover: none) and (pointer: coarse) {
          .hover\\:scale-\\[1\\.01\\]:hover {
            transform: none;
          }
          
          .hover\\:scale-\\[1\\.01\\]:active {
            transform: scale(0.98);
          }
        }

        /* Better text wrapping on mobile */
        .truncate {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* Ensure proper spacing on very small screens */
        @media (max-width: 375px) {
          .space-x-2 > :not([hidden]) ~ :not([hidden]) {
            margin-left: 0.5rem;
          }
          
          .space-y-2 > :not([hidden]) ~ :not([hidden]) {
            margin-top: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
}